/**
 * Shared date utilities for the Website tab.
 *
 * The API returns naive UTC datetimes (e.g. "2026-07-15T10:30:00")
 * per docs/adr/0002-naive-utc-datetime-convention.md.  JavaScript's
 * `new Date(str)` parses strings without a timezone designator as
 * *local* time, which shifts relative-time calculations by the user's
 * UTC offset.  `parseUtcDate` normalises this by appending "Z" when
 * no timezone info is present.
 */

/** Parse a naive-UTC ISO string from the API into a Date. */
export function parseUtcDate(iso: string): Date {
  if (/[Zz]$/.test(iso) || /[+-]\d{2}(:\d{2})?$/.test(iso)) {
    return new Date(iso);
  }
  return new Date(iso + "Z");
}

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Format an API datetime as a long date (e.g. "Jul 15, 2026").
 * Displayed in the user's local timezone.
 */
export function formatLongDate(iso: string | null): string {
  if (!iso) return "—";
  const d = parseUtcDate(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${SHORT_MONTHS[d.getMonth()]} ${String(d.getDate()).padStart(2, "0")}, ${d.getFullYear()}`;
}

/** Format an API datetime as a past-relative string (e.g. "5m ago"). */
export function relativeFromNow(iso: string | null): string | null {
  if (!iso) return null;
  const ms = Date.now() - parseUtcDate(iso).getTime();
  return formatDuration(ms, "ago");
}

/** Format an API datetime as a future-relative string (e.g. "3h"). */
export function relativeUntil(iso: string): string | null {
  const ms = parseUtcDate(iso).getTime() - Date.now();
  if (ms <= 0) return null;
  return formatDuration(ms, null);
}

/**
 * Format an API datetime as a short date (e.g. "Jul 15").
 * Month + zero-padded day, no year. Displayed in the user's local timezone.
 */
export function formatShortDate(iso: string): string {
  const d = parseUtcDate(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${SHORT_MONTHS[d.getMonth()]} ${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Format an API datetime as a full timestamp (e.g. "Jul 15, 2026 14:30").
 * Displayed in the user's local timezone.
 */
export function formatTimestamp(iso: string | null): string | null {
  if (!iso) return null;
  const d = parseUtcDate(iso);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${SHORT_MONTHS[d.getMonth()]} ${pad(d.getDate())}, ${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Convert an API datetime to a `datetime-local` input value
 * (e.g. "2026-07-15T14:30").  Uses `parseUtcDate` to correctly
 * interpret the naive-UTC string before extracting local components.
 */
export function toLocalDateTime(iso: string | null): string {
  if (!iso) return "";
  const d = parseUtcDate(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDuration(ms: number, suffix: "ago" | null): string | null {
  if (Number.isNaN(ms)) return null;
  const abs = Math.abs(ms);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  let value: string;
  if (abs < hour) value = `${Math.max(1, Math.floor(abs / minute))}m`;
  else if (abs < day) value = `${Math.floor(abs / hour)}h`;
  else if (abs < 30 * day) value = `${Math.floor(abs / day)} days`;
  else if (abs < 365 * day)
    value = `${Math.floor(abs / (30 * day))} months`;
  else value = `${Math.floor(abs / (365 * day))} years`;
  return suffix ? `${value} ${suffix}` : value;
}
